import { MAX_CONCURRENCY } from "@/constants/evaluation-concurrency"
import { MAX_ITERATION_COUNT } from "@/constants/iteration-limits"

const DIGITS_ONLY_REGEX = /[^0-9]/g

export const MAX_ITERATIONS = MAX_ITERATION_COUNT
export const DEFAULT_CONCURRENCY_LIMIT = 5
const ORG_OVERRIDABLE_SIMULATOR_CONCURRENCY_LIMIT = MAX_CONCURRENCY

export const TEST_CASE_MODE = {
  ALL: "all",
  SELECT: "select",
  SUBSAMPLE: "subsample",
} as const

export type TestCaseMode = (typeof TEST_CASE_MODE)[keyof typeof TEST_CASE_MODE]

const CONCURRENCY_LIMITS_BY_SIMULATOR: Record<string, number> = {
  MODEL_TYPE_VOICE: ORG_OVERRIDABLE_SIMULATOR_CONCURRENCY_LIMIT,
  MODEL_TYPE_OUTBOUND_VOICE: ORG_OVERRIDABLE_SIMULATOR_CONCURRENCY_LIMIT,
  MODEL_TYPE_DAILY: 5,
  MODEL_TYPE_LIVEKIT: 3,
  MODEL_TYPE_CHAT: 10,
  MODEL_TYPE_CHAT_A2A: 10,
  MODEL_TYPE_EMAIL: 10,
  MODEL_TYPE_WEBSOCKET: 8,
  MODEL_TYPE_CHAT_WEBSOCKET: 8,
  MODEL_TYPE_NOOP: ORG_OVERRIDABLE_SIMULATOR_CONCURRENCY_LIMIT,
  MODEL_TYPE_SMS: 10,
  MODEL_TYPE_VOICE_TO_VOICE: 10,
  MODEL_TYPE_OPENAI_REALTIME: 10,
  MODEL_TYPE_GEMINI_REALTIME: 10,
  MODEL_TYPE_DEEPGRAM_REALTIME: 10,
  // Historical caps for simulator types the backend no longer executes.
  // Legacy agents configured with these types stay selectable in the agent
  // picker, and their raw model_type flows through this lookup. Without these
  // entries the cap falls to the default 5, which clamps the user's saved
  // concurrency down and leaves it reduced even after they switch back to a
  // supported agent.
  MODEL_TYPE_TTS: 50,
  MODEL_TYPE_OPENAI_ENDPOINT: 10,
}

interface TestCaseModeTransition {
  shouldClearSelectedTestCaseIds: boolean
  subSamplingUpdate: {
    shouldSubSample: boolean
    subSampleSize?: number
  }
}

interface IterationInputChangeParams {
  rawValue: string
  concurrency: number
  maxConcurrencyLimit: number
}

interface IterationInputBlurParams {
  inputValue: string
  concurrency: number
  maxConcurrencyLimit: number
}

interface NumericInputChangeResult {
  cleanValue: string
  numericValue?: number
}

interface NumericInputBlurResult {
  nextInputValue: string
  nextNumericValue: number
}

interface EffectiveTestCaseCountParams {
  mode: TestCaseMode
  selectedTestCaseCount: number
  subSampleSize?: number
  testCaseCount: number
}

export function getInitialTestCaseMode(
  hasSpecificTestCases: boolean,
  shouldSubSample: boolean,
): TestCaseMode {
  if (hasSpecificTestCases) {
    return TEST_CASE_MODE.SELECT
  }
  if (shouldSubSample) {
    return TEST_CASE_MODE.SUBSAMPLE
  }
  return TEST_CASE_MODE.ALL
}

export function getSyncedTestCaseMode(
  currentMode: TestCaseMode,
  hasSpecificTestCases: boolean,
  shouldSubSample: boolean,
): TestCaseMode {
  if (hasSpecificTestCases) {
    return TEST_CASE_MODE.SELECT
  }

  if (shouldSubSample) {
    return TEST_CASE_MODE.SUBSAMPLE
  }

  return currentMode
}

export function getTestCaseModeTransition(
  nextMode: TestCaseMode,
  subSampleSize?: number,
): TestCaseModeTransition {
  if (nextMode === TEST_CASE_MODE.ALL) {
    return {
      shouldClearSelectedTestCaseIds: true,
      subSamplingUpdate: { shouldSubSample: false, subSampleSize: undefined },
    }
  }
  if (nextMode === TEST_CASE_MODE.SELECT) {
    return {
      shouldClearSelectedTestCaseIds: false,
      subSamplingUpdate: { shouldSubSample: false, subSampleSize: undefined },
    }
  }
  return {
    shouldClearSelectedTestCaseIds: true,
    subSamplingUpdate: {
      shouldSubSample: true,
      subSampleSize: subSampleSize || 1,
    },
  }
}

export function getMaxConcurrencyForSimulator(type: string): number {
  return CONCURRENCY_LIMITS_BY_SIMULATOR[type] || DEFAULT_CONCURRENCY_LIMIT
}

export function getTestSetSelectorMaxConcurrencyLimit(
  simulatorType: string,
  maxConcurrency?: number,
): number {
  const simulatorMaxConcurrency = getMaxConcurrencyForSimulator(simulatorType)

  if (maxConcurrency == null) {
    return simulatorMaxConcurrency
  }

  if (
    maxConcurrency > simulatorMaxConcurrency &&
    simulatorMaxConcurrency === ORG_OVERRIDABLE_SIMULATOR_CONCURRENCY_LIMIT
  ) {
    return maxConcurrency
  }

  return Math.min(maxConcurrency, simulatorMaxConcurrency)
}

function sanitizeNumericInput(value: string): string {
  return value.replace(DIGITS_ONLY_REGEX, "")
}

export function getIterationInputChangeResult({
  rawValue,
  concurrency,
  maxConcurrencyLimit,
}: IterationInputChangeParams): {
  cleanValue: string
  nextIterationCount?: number
  nextConcurrency?: number
} {
  const cleanValue = sanitizeNumericInput(rawValue)
  if (cleanValue === "") {
    return { cleanValue }
  }

  const numValue = parseInt(cleanValue, 10)
  if (Number.isNaN(numValue) || numValue < 1 || numValue > MAX_ITERATIONS) {
    return { cleanValue }
  }

  return {
    cleanValue,
    nextIterationCount: numValue,
    nextConcurrency:
      concurrency > maxConcurrencyLimit ? maxConcurrencyLimit : undefined,
  }
}

export function getIterationBlurResult({
  inputValue,
  concurrency,
  maxConcurrencyLimit,
}: IterationInputBlurParams): {
  nextInputValue: string
  nextIterationCount: number
  nextConcurrency?: number
} | null {
  const numericValue = parseInt(inputValue, 10)

  if (inputValue !== "" && numericValue >= 1) {
    if (numericValue > MAX_ITERATIONS) {
      return {
        nextInputValue: String(MAX_ITERATIONS),
        nextIterationCount: MAX_ITERATIONS,
        nextConcurrency:
          concurrency > maxConcurrencyLimit ? maxConcurrencyLimit : undefined,
      }
    }

    return null
  }

  return {
    nextInputValue: "1",
    nextIterationCount: 1,
    nextConcurrency:
      concurrency > maxConcurrencyLimit ? maxConcurrencyLimit : undefined,
  }
}

export function getNumericInputChangeResult(
  rawValue: string,
  maxValue?: number,
): NumericInputChangeResult {
  const cleanValue = sanitizeNumericInput(rawValue)
  if (cleanValue === "") {
    return { cleanValue }
  }

  const numericValue = parseInt(cleanValue, 10)
  if (Number.isNaN(numericValue) || numericValue < 1) {
    return { cleanValue }
  }

  if (maxValue !== undefined && numericValue > maxValue) {
    return { cleanValue: String(maxValue), numericValue: maxValue }
  }

  return { cleanValue, numericValue }
}

export function getNumericInputBlurResult(
  inputValue: string,
  maxValue?: number,
): NumericInputBlurResult | null {
  const numericValue = parseInt(inputValue, 10)

  if (inputValue !== "" && numericValue >= 1) {
    if (maxValue !== undefined && numericValue > maxValue) {
      return {
        nextInputValue: String(maxValue),
        nextNumericValue: maxValue,
      }
    }

    return null
  }

  return {
    nextInputValue: "1",
    nextNumericValue: 1,
  }
}

export function getSubSampleInputChangeResult(rawValue: string): {
  cleanValue: string
  subSamplingUpdate?: { shouldSubSample: boolean; subSampleSize: number }
} {
  const cleanValue = sanitizeNumericInput(rawValue)
  if (cleanValue === "") {
    return { cleanValue }
  }

  const numericValue = parseInt(cleanValue, 10)
  if (Number.isNaN(numericValue) || numericValue <= 0) {
    return { cleanValue }
  }

  return {
    cleanValue,
    subSamplingUpdate: { shouldSubSample: true, subSampleSize: numericValue },
  }
}

export function getSubSampleInputBlurResult(inputValue: string): {
  nextInputValue: string
  subSamplingUpdate: { shouldSubSample: boolean; subSampleSize: number }
} | null {
  if (inputValue !== "" && parseInt(inputValue, 10) > 0) {
    return null
  }
  return {
    nextInputValue: "1",
    subSamplingUpdate: { shouldSubSample: true, subSampleSize: 1 },
  }
}

export function isTestSetSelectionValid(testSetId: string): boolean {
  return testSetId !== ""
}

export function getEffectiveTestCaseCount({
  mode,
  selectedTestCaseCount,
  subSampleSize,
  testCaseCount,
}: EffectiveTestCaseCountParams): number {
  if (mode === TEST_CASE_MODE.SELECT) {
    return selectedTestCaseCount
  }

  if (mode === TEST_CASE_MODE.SUBSAMPLE && subSampleSize) {
    return Math.min(subSampleSize, testCaseCount)
  }

  return testCaseCount
}

export function getTotalScenarios(
  effectiveTestCaseCount: number,
  iterationCount: number,
): number {
  return effectiveTestCaseCount * iterationCount
}
